import Vue from 'vue'
import {
  init,
  browserTracingIntegration,
  getClient,
  captureException,
  captureMessage,
  addBreadcrumb,
  setTag,
  setUser,
  logger,
  getTraceData,
} from '@sentry/vue'
import { createSentry } from '@ahaslides-product/frontend-toolkit'

// configs
import configs from '@/config'

// Load the replay integration lazily (its own async chunk) so the heavy replay
// recorder stays out of the initial parse. See services/sentryReplay.js.
const loadReplayIntegration = () => import(/* webpackChunkName: "sentry-replay" */ '@/services/sentryReplay')
  .then((m) => m.replayIntegration)

// Casting URLs are '/casting/:id/:shareCode' — collapse the volatile id +
// shareCode segments so every rejection on the same route groups together.
const normalizeCastingUrl = (url) => {
  if (typeof url !== 'string' || !url) return undefined

  try {
    const { pathname } = new URL(url, 'https://placeholder.local')
    return pathname.replace(/\/casting\/[^/]+\/[^/]+/, '/casting/:id/:shareCode')
  } catch {
    return undefined
  }
}

// Require all three casting keys, not any one: a vue-resource Response has
// url + status but no type/error, so matching on a single field mislabels it.
const isCastingRejection = (error) => (
  error != null &&
  typeof error === 'object' &&
  !(error instanceof Error) &&
  'error' in error &&
  'type' in error &&
  'url' in error
)

// Somewhere in the casting flow a Promise rejects with a plain object
// ({ error, type, url }) instead of an Error, so Sentry's global
// onunhandledrejection handler can't extract a type — every such rejection
// collapses into one un-triageable "<unknown>" group. Relabel it into a
// stable, groupable issue instead of dropping it (a real casting failure
// still needs to surface). Mirrors the vue-resource Response classification
// in stpancras-audience-app's beforeSend (SYST-222).
const relabelCastingRejectionEvent = (event, error) => {
  const rejectionType = typeof error.type === 'string' && error.type
    ? error.type
    : 'CastingPromiseRejection'
  const path = normalizeCastingUrl(error.url)
  const innerMessage = typeof error.error === 'string'
    ? error.error
    : error.error?.message

  event.exception = {
    ...event.exception,
    values: [{
      ...event.exception?.values?.[0],
      type: 'CastingPromiseRejection',
      value: `${rejectionType}${path ? ` ${path}` : ''}${innerMessage ? `: ${innerMessage}` : ''}`,
    }],
  }
  event.fingerprint = ['casting-promise-rejection', rejectionType, path || 'unknown']
  event.extra = {
    ...event.extra,
    castingRejection: { type: rejectionType, url: path, error: innerMessage },
  }

  return event
}

// beforeSend runs in the browser on MINIFIED frames (e.g. /js/chunk-vendors.<hash>.js);
// node_modules paths only appear after Sentry's server-side symbolication. So every
// third-party frame matcher below also accepts a signal that survives minification.
const frameFunctionName = (frame) => (
  typeof frame?.function === 'string' ? frame.function.split('.').pop() : ''
)

const PICKR_NULL_DEREFERENCE_MESSAGES = new Set([
  "Cannot read properties of null (reading 'clone')",
  "Cannot read properties of null (reading 'changestop')",
  'can\'t access property "clone" of null',
])

// Safari drops pickr's _tapmove frame from the stack, so these wordings are
// matched without a frame: they name pickr's private fields, which no app code uses.
const PICKR_PRIVATE_FIELD_MESSAGES = new Set([
  'can\'t access property "clone", this._lastColor is null',
  'can\'t access property "changestop", this._eventListener is null',
  "null is not an object (evaluating 'this._lastColor.clone')",
])

const PICKR_POINTER_HANDLER_NAMES = new Set(['_tapmove', '_tapstop'])

const isPickrFrame = (frame) => (
  (typeof frame?.filename === 'string' && frame.filename.includes('@simonwep/pickr')) ||
  PICKR_POINTER_HANDLER_NAMES.has(frameFunctionName(frame))
)

// @simonwep/pickr dereferences null inside _tapmove/_tapstop when a pointer
// event fires after the picker has already been destroyed. Every frame is
// third-party, so a generic null-dereference message must also carry a pickr
// frame — a same-message error from our own bundle still reports.
export const isPickrTeardownEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => (
    PICKR_PRIVATE_FIELD_MESSAGES.has(value?.value) ||
    (
      PICKR_NULL_DEREFERENCE_MESSAGES.has(value?.value) &&
      (value.stacktrace?.frames || []).some(isPickrFrame)
    )
  ))
}

const dropPickrTeardown = (event) => (isPickrTeardownEvent(event) ? null : event)

const relabelCastingRejection = (event, hint) => {
  const error = hint?.originalException
  return isCastingRejection(error) ? relabelCastingRejectionEvent(event, error) : event
}

export default createSentry({
  // Named members, not the namespace: passing `import * as` as a value keeps every export, the replay recorder included.
  Sentry: {
    init,
    browserTracingIntegration,
    getClient,
    captureException,
    captureMessage,
    addBreadcrumb,
    setTag,
    setUser,
    logger,
    getTraceData,
  },
  Vue,
  config: {
    enabled: configs.sentryEnabled,
    release: configs.sentryRelease,
    dsn: configs.sentry,
    environment: configs.env,
    tracesSampleRate: 0.02,
    propagateTraceparent: true,
    logErrorsToConsole: true,
    ignoreErrors: [
      'The operation is insecure.',
      'fullscreen error',
      'null is not an object (evaluating \'a.error\')',
      'UET is not defined',
      'too much recursion',
      'Fullscreen request denied',
      'Maximum call stack size exceeded',
      'Unable to decode audio data',
      'Extension context invalidated.',
      'xhr poll error',
      'Cannot read property \'getReadModeConfig\' of undefined',
      '"Timeout"',
      'UnhandledRejection: Non-Error promise rejection captured with value: Timeout',
      'AbortError: The play() request was interrupted by a call to pause().',
      'can\'t access dead object',
      'this.editor is undefined',
      'Cannot read properties of undefined (reading \'setMode\')',
      'undefined is not an object (evaluating \'this.editor.setMode\')',
      'Detected container element removed from DOM',
    ],
    denyUrls: [
      /analytics\.tiktok\.com/i,
      /js\.zohocdn\.com/i,
      /salesiq\.zoho\.com/i,
      /www\.gstatic\.com/i,
      /googletagmanager\.com/i,
      /\/gtm\.js(\?|$)/i,
      /\/gtag\/js(\?|$)/i,
    ],
    // No replay in the PowerPoint add-in. With replaysOnErrorSampleRate set, every page records
    // into the error buffer, not 20% of them, and the recorder starts with a full snapshot that
    // serialises the whole stylesheet (several MB) on the main thread, in the middle of the boot.
    // A deck boots one add-in page per add-in slide, several at once when it opens.
    replay: { onErrorSampleRate: 0.2, enabled: !configs.isEnableAddinPowerpoint, load: loadReplayIntegration },
  },
  appRules: [dropPickrTeardown, relabelCastingRejection],
})
